// Page LCA (lot B9) : l'établi de la lecture critique d'article. Quatre postes sous une barre d'onglets de page —
// les annales, l'entraînement, le cours, les podcasts. Un sujet est une ligne dense (l'article, sa revue, ses thèmes,
// sa réussite) depuis laquelle on part travailler : la séance elle-même appartient au moteur de QCM
// (qcmStartSession, qcmOpenItem), cette page ne la rejoue pas.
//
// Ce module remplace window.renderLcaTab et les actions lca* du rendu historique (src/legacy/lca.js, dont les corps
// de rendu sont vidés). Il garde les fonctions de domaine et de chargement qui y vivent : lcaSujets, estSujetLca,
// lcaChargerCours, lcaChargerCoursPro, LCA_CHAPITRES, lcaChapitrePodcast.
import { html, attr } from '../../core/html.js';
import { state } from '../../core/store.js';
import { actions } from '../../core/actions.js';
import { ico } from '../icones.js';

var focusARestaurer = null;   // sélection du champ de recherche quand la page entière se redessine pendant la frappe
var renduPrevu = 0;           // rAF en attente pour la frappe

// ---------------------------------------------------------------- état de l'onglet
function etat() {
    state.lca = state.lca || {};
    var L = state.lca;
    if (!L.onglet) L.onglet = 'annales';
    if (L.filtre == null) L.filtre = '';
    if (L.page == null) L.page = 1;
    if (L.livreChoix == null) L.livreChoix = 'tous';
    if (L.livreArticles == null) L.livreArticles = true;
    if (L.livreDensite == null) L.livreDensite = 'compact';
    return L;
}
window.lcaEtat = etat;

var POSTES = [
    ['annales', 'Annales'],
    ['entrainement', 'Entraînement'],
    ['cours', 'Cours'],
    ['podcasts', 'Podcasts'],
];
function estListe(o) { return o === 'annales' || o === 'entrainement'; }
function pluriel(n, un, des) { return n + ' ' + (n > 1 ? des : un); }
function normaliser(s) { return String(s == null ? '' : s).toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, ''); }
function admin() { return !!(window.estAdmin && window.estAdmin()); }
function mouvementReduit() { try { return window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch (e) { return false; } }
function sujets(g) { return window.lcaSujets ? window.lcaSujets(g) : []; }
// Typographie française : l'espace qui précède ; : ! ? est fine et insécable (U+202F). Sans elle, le navigateur
// coupe devant le signe et une phrase peut commencer par un point-virgule (vu au contrôle des captures).
function fr(t) { return String(t == null ? '' : t).replace(/ ([;:!?])/g, ' $1'); }
function indexQcm() { return (state.qcm && state.qcm.index) || {}; }
function genre(n, ix) {
    var t = ix && ix.lca ? String(ix.lca.type || '').toLowerCase() : '';
    if (t.indexOf('annale') === 0) return 'annales';
    if (t) return 'entrainement';
    var plages = window.LCA_PLAGES || { annales: [2001, 2499] };
    return parseInt(n, 10) <= plages.annales[1] ? 'annales' : 'entrainement';
}

// ---------------------------------------------------------------- la page
export function renderLca() {
    var L = etat();
    var actif = document.activeElement;
    // Une réponse du serveur peut redessiner la page entière pendant la frappe : la sélection du champ est retenue
    // ici et rendue à l'image suivante, quand le nouveau champ existe (renderContent ne rappelle pas ce module).
    if (actif && actif.id === 'lca-recherche') { focusARestaurer = { debut: actif.selectionStart, fin: actif.selectionEnd }; requestAnimationFrame(rendreFocus); }
    if (state.qcm && !state.qcm.index && !state.qcm.indexLoading && window.loadQcmIndex) window.loadQcmIndex(function() { window.renderContent(); });
    if ((L.onglet === 'cours' || L.onglet === 'podcasts') && state.podcastsConfigured === null && window.loadPodcastIndex) window.loadPodcastIndex(function() { window.renderContent(); });
    if (L.onglet === 'cours' && !L.coursCharge && !L.coursBusy && window.lcaChargerCours) window.lcaChargerCours();

    var corps;
    if (L.onglet === 'cours') corps = panneauCours();
    else if (L.onglet === 'podcasts') corps = panneauPodcasts();
    else corps = panneauSujets(L.onglet);

    return html`<div class="lca fade-in">
        ${entete()}
        ${barrePostes(L.onglet)}
        ${panneauLivre()}
        <div class="lca-panneau" id="lca-panneau" role="tabpanel" aria-labelledby="lca-poste-${L.onglet}" tabindex="-1">${corps}</div>
    </div>`;
}

// Les deux actions de la page vivent dans l'en-tête, pas dans la marche à suivre : elles valent depuis les quatre
// postes, et la marche redevient la ligne unique qu'elle doit être.
function entete() {
    var L = etat(), charge = !!(state.qcm && state.qcm.indexLoading);
    var nA = sujets('annales').length, nE = sujets('entrainement').length;
    var nC = (window.LCA_CHAPITRES || []).length;
    return html`<header class="jour-entete lca-entete">
        <div class="jour-entete-texte">
            <h1 class="jour-date">Lecture critique d'article</h1>
            <p class="jour-resume num">${charge ? 'Lecture des sujets…' : html`${nA ? pluriel(nA, 'annale', 'annales') : 'Aucune annale'} · ${nE ? pluriel(nE, 'entraînement', 'entraînements') : 'aucun entraînement'} · ${pluriel(nC, 'chapitre de cours', 'chapitres de cours')}`}</p>
        </div>
        <div class="lca-entete-actions">
            <button type="button" class="btn btn-fantome btn-sm" data-action="lcaMemoComplet" title="Le mémo de méthode, dans le poste Cours">${ico('brain', { taille: 16 })}Le mémo complet</button>
            <button type="button" class="btn btn-fantome btn-sm" data-action="lcaLivre" aria-expanded="${L.livre ? 'true' : 'false'}" aria-controls="lca-livre" title="Réunir les sujets en un livre imprimable : l'article, les questions, puis les corrections">${ico('file-pdf', { taille: 16 })}Le livre de LCA</button>
        </div>
    </header>`;
}

// La barre des quatre postes : la grammaire de l'onglet du rail, posée une marche plus bas (le composant partagé est
// cloué à la hauteur du rail et ne se réemploie pas ici — voir « Non résolu » du brief).
function barrePostes(courant) {
    var comptes = {
        annales: sujets('annales').length,
        entrainement: sujets('entrainement').length,
        podcasts: (window.LCA_CHAPITRES || []).filter(function(c) { var pc = window.lcaChapitrePodcast(c[0]); return pc && pc.status === 'ready'; }).length,
    };
    return html`<div class="lca-onglets" role="tablist" aria-label="Les quatre postes de la LCA">${POSTES.map(function(p) {
        var on = p[0] === courant, n = comptes[p[0]];
        return html`<button type="button" class="lca-onglet" id="lca-poste-${p[0]}" role="tab" aria-selected="${on ? 'true' : 'false'}" aria-controls="lca-panneau" tabindex="${on ? '0' : '-1'}" data-action="lcaOnglet" data-args="${attr([p[0]])}">${p[1]}${n ? html`<span class="lca-onglet-compte num">${n}</span>` : ''}</button>`;
    })}</div>`;
}

// ---------------------------------------------------------------- annales et entraînement
function panneauSujets(g) {
    var L = etat();
    return html`${marcheASuivre()}
        <div class="lca-outils" role="search" aria-label="Chercher un sujet de LCA">
            <div class="lca-recherche">
                <label for="lca-recherche" class="sr-only">Chercher un sujet</label>
                ${ico('magnifying-glass', { taille: 18 })}
                <input id="lca-recherche" class="saisie" type="text" value="${L.filtre}" placeholder="Article, revue, thème, année" autocomplete="off" spellcheck="false" enterkeyhint="search" data-input-action="lcaFiltre" aria-describedby="lca-compte">
                <button type="button" class="ico-btn lca-effacer" data-action="lcaEffacer" aria-label="Effacer la recherche"${L.filtre ? '' : html` hidden`}>${ico('x', { taille: 18 })}</button>
            </div>
            <p id="lca-compte" class="lca-compte num">${texteCompte(sujets(g).length, '', g)}</p>
            <p id="lca-compte-annonce" class="sr-only" role="status" aria-live="polite" aria-atomic="true"></p>
        </div>
        <div id="lca-liste" class="lca-liste">${listeHtml(g)}</div>`;
}

// La marche à suivre : quatre étapes numérotées, parce que l'ordre EST l'information (on ne lit pas un article
// dans le désordre). Ce n'est pas un sur-titre décoratif.
var MARCHE = ["Objectif et schéma d'étude", 'Méthodes en entier', 'Tableau 1 et critère principal', 'Le reste, en vérification'];
function marcheASuivre() {
    return html`<section class="lca-marche" aria-labelledby="lca-marche-titre">
        <h2 id="lca-marche-titre" class="lca-marche-titre">La marche à suivre</h2>
        <ol class="lca-marche-etapes">${MARCHE.map(function(e, i) {
            return html`<li class="lca-marche-etape"><span class="lca-marche-rang num" aria-hidden="true">${i + 1}</span>${e}</li>`;
        })}</ol>
    </section>`;
}

// Le livre : un panneau en ligne, déplié sur place. Régler un export n'exige ni interruption ni focus protégé :
// ce n'est pas une fenêtre.
var DENSITES = [['compact', 'Compact', 'deux colonnes, texte serré'], ['confort', 'Confort', 'une colonne, texte aéré']];
function optionsLivre() {
    var nA = sujets('annales').length, nE = sujets('entrainement').length;
    return [
        ['tous', 'Les deux parties', 'Partie I : les annales. Partie II : les entraînements, chacune ouverte par sa page de garde.', nA + nE],
        ['annales', 'Les annales seules', 'Les sujets tombés aux épreuves.', nA],
        ['entrainement', 'Les entraînements seuls', 'Les sujets d\'entraînement.', nE],
    ];
}
function resumeLivre() {
    var L = etat(), opt = optionsLivre().find(function(o) { return o[0] === L.livreChoix; }) || optionsLivre()[0];
    return pluriel(opt[3], 'sujet', 'sujets') + ' · ' + (L.livreArticles ? 'articles joints' : 'sans les articles') + ' · ' + (L.livreDensite === 'confort' ? 'une colonne' : 'deux colonnes');
}
function panneauLivre() {
    var L = etat();
    if (!L.livre) return html`<div class="lca-livre" id="lca-livre" hidden></div>`;
    return html`<div class="lca-livre carte" id="lca-livre" role="region" aria-label="Le livre de LCA"><div class="carte-corps">
        <h2 class="lca-livre-titre">Le livre de LCA</h2>
        <p class="lca-livre-aide">${fr("Chaque sujet donne son article, ses questions, puis ses corrections. Le livre s'ouvre dans un onglet ; c'est l'impression du navigateur qui en fait le PDF.")}</p>
        <fieldset class="lca-livre-groupe">
            <legend>Ce qu'on sort</legend>
            ${optionsLivre().map(function(o) {
                return html`<label class="lca-option${L.livreChoix === o[0] ? ' lca-option--on' : ''}">
                    <input type="radio" name="lca-livre-choix" value="${o[0]}"${L.livreChoix === o[0] ? html` checked` : ''} data-change-action="lcaLivreChoix" data-args="${attr([o[0]])}">
                    <span class="lca-option-texte"><span class="lca-option-titre">${o[1]}<span class="lca-option-compte num">${pluriel(o[3], 'sujet', 'sujets')}</span></span><span class="lca-option-detail">${fr(o[2])}</span></span>
                </label>`;
            })}
        </fieldset>
        <fieldset class="lca-livre-groupe">
            <legend>Comment on l'imprime</legend>
            <label class="lca-option">
                <input type="checkbox"${L.livreArticles ? html` checked` : ''} data-change-action="lcaLivreArticles">
                <span class="lca-option-texte"><span class="lca-option-titre">Joindre les articles</span><span class="lca-option-detail">${fr("Chacun est imprimé en entier, juste avant ses questions. Le navigateur les dessine page par page à l'ouverture : compte quelques secondes par article.")}</span></span>
            </label>
            <div class="lca-livre-densite">
                <span class="lca-champ-label" id="lca-densite-label">Densité</span>
                <div class="segments" role="tablist" aria-labelledby="lca-densite-label">${DENSITES.map(function(d) {
                    return html`<button type="button" role="tab" aria-selected="${L.livreDensite === d[0] ? 'true' : 'false'}" tabindex="${L.livreDensite === d[0] ? '0' : '-1'}" data-action="lcaLivreDensite" data-args="${attr([d[0]])}" title="${d[2]}">${d[1]}</button>`;
                })}</div>
            </div>
        </fieldset>
        <div class="lca-livre-pied">
            <p class="lca-livre-resume num" id="lca-livre-resume">${resumeLivre()}</p>
            <div class="lca-livre-actions">
                <button type="button" class="btn btn-fantome btn-sm" data-action="lcaLivre">Annuler</button>
                <button type="button" class="btn btn-primaire btn-sm" data-action="lcaLivreOuvrir">Ouvrir le livre</button>
            </div>
        </div>
    </div></div>`;
}

function texteCompte(n, f, g) {
    var t = n ? pluriel(n, 'sujet', 'sujets') : (g === 'annales' ? "Aucune annale" : "Aucun entraînement");
    if (f) t += ' pour « ' + f + ' »';
    return t;
}
function correspond(n, ix, f) {
    if (!f) return true;
    var l = (ix && ix.lca) || {}, a = l.article || {};
    var foin = normaliser([n, ix && ix.titre, l.titre, l.annee, a.titre, a.revue, a.auteurs, (l.themes || []).join(' ')].join(' '));
    return foin.indexOf(f) !== -1;
}

function listeHtml(g) {
    var L = etat(), idx = indexQcm();
    if (state.qcm && state.qcm.indexLoading) return squelettes();
    var tous = sujets(g);
    if (!tous.length) return videAucunSujet(g);
    var f = normaliser(L.filtre.trim());
    var vus = tous.filter(function(n) { return correspond(n, idx[n], f); });
    if (!vus.length) return html`<div class="vide">${ico('magnifying-glass')}<h2>Aucun sujet ne correspond</h2><p>Essaie le nom d'une revue, un thème (« essai randomisé », « méta-analyse »), une année, ou le numéro du sujet.</p><button type="button" class="btn btn-fantome" data-action="lcaEffacer">Effacer la recherche</button></div>`;
    var M = window.computeMastery ? window.computeMastery() : {};
    return html`<div class="lca-rangs" role="list" aria-label="Sujets de LCA">${vus.map(function(n) { return ligne(n, idx[n] || {}, M[n]); })}</div>
        <p class="lca-pied num">${texteCompte(vus.length, '', g)}${vus.length !== tous.length ? html` sur ${tous.length}` : ''}. ${fr('Les corrections viennent de LCA Pro ; le barème est celui des EDN.')}</p>`;
}

function squelettes() {
    return html`<div class="squelettes" aria-hidden="true">${[1, 2, 3, 4, 5].map(function() {
        return html`<div class="lca-sujet lca-sujet--squelette"><div class="lca-sujet-tete"><div class="squelette squelette-ligne" style="width:62%"></div></div><div class="lca-sujet-meta"><div class="squelette squelette-ligne" style="width:34%;height:18px"></div></div></div>`;
    })}</div>`;
}

// L'état vide est écrit pour l'étudiant d'abord ; la manœuvre de dépôt n'apparaît que pour l'administrateur, qui est
// le seul à pouvoir la faire.
function videAucunSujet(g) {
    var quoi = g === 'annales' ? "d'annales" : "d'entraînement";
    return html`<div class="vide">${ico('newspaper')}
        <h2>Aucun sujet ${quoi} pour l'instant</h2>
        <p>${fr("Les sujets de LCA sont déposés par lots. En attendant, le poste Cours contient le polycopié, le mémo de méthode et les treize chapitres en podcast : c'est la partie qui s'apprend.")}</p>
        <button type="button" class="btn btn-fantome" data-action="lcaOnglet" data-args="${attr(['cours'])}">Aller au cours</button>
        ${admin() ? html`<p class="lca-vide-admin">Dépôt : <code>scripts/lcapro_scraper.py</code> (dépôt QCM, abonnement lcapro.fr) écrit les fichiers <code>NNNN - LCA … .json</code> dans <code>edn_data/qcm/</code>, et le contenu de <code>images/</code> dans <code>edn_data/qcm/images/</code>. Les annales portent les numéros 2001 à 2499, les entraînements 2501 à 2999.</p>` : ''}
    </div>`;
}

// Une ligne par sujet : le numéro, l'article, ses repères, et les actions qui partent vers le moteur de QCM.
function ligne(n, ix, M) {
    var l = ix.lca || {}, a = l.article || {};
    var titre = a.titre || l.titre || ix.titre || ('Sujet ' + n);
    var g = genre(n, ix);
    var reussite = (M && M.mastery != null && M.n) ? Math.round(M.mastery * 100) : null;
    var pdf = a.pdf && window.qcmImageUrl ? window.qcmImageUrl(a.pdf) : '';
    var nb = ix.nb || 0;
    return html`<div class="lca-sujet" role="listitem" data-sujet="${n}">
        <div class="lca-sujet-tete">
            <span class="lca-sujet-num num">${n}</span>
            <span class="lca-sujet-nom">${titre}</span>
            ${a.auteurs ? html`<span class="lca-sujet-auteurs">${a.auteurs}</span>` : ''}
        </div>
        <div class="lca-sujet-meta">
            <span class="puce puce-sans-point lca-genre num">${g === 'annales' ? html`Annale${l.annee ? html` ${l.annee}` : ''}` : 'Entraînement'}</span>
            ${a.revue ? html`<span class="puce puce-sans-point lca-revue">${a.revue}</span>` : ''}
            ${(l.themes || []).slice(0, 3).map(function(t) { return html`<span class="puce puce-sans-point lca-theme">${t}</span>`; })}
            <span class="puce puce-sans-point lca-volume num">${pluriel(nb, 'question', 'questions')}${l.duree ? html` · ${l.duree} min` : ''}</span>
            ${reussite != null ? html`<span class="puce ${reussite >= 75 ? 'puce-ok' : (reussite >= 50 ? 'puce-warn' : 'puce-danger')} num" title="${reussite} % de réussite sur ${pluriel(M.n, 'réponse', 'réponses')} à ce sujet">Réussite ${reussite} %</span>` : ''}
        </div>
        <div class="lca-sujet-actions">
            ${pdf ? html`<a class="btn btn-fantome btn-sm" href="${pdf}" target="_blank" rel="noopener" title="Ouvrir l'article du sujet ${n} en PDF">${ico('file-pdf', { taille: 16 })}<span class="lca-action-texte">L'article</span></a>` : ''}
            <button type="button" class="btn btn-fantome btn-sm" data-action="lcaCorrections" data-args="${attr([String(n)])}" title="Lire les questions du sujet ${n} et leurs corrections, sans chronomètre">${ico('list-checks', { taille: 16 })}<span class="lca-action-texte">Corrections</span></button>
            <button type="button" class="btn btn-secondaire btn-sm lca-faire" data-action="lcaFaire" data-args="${attr([String(n)])}" title="${reussite != null ? 'Refaire' : 'Faire'} le sujet ${n} en conditions réelles, dans le moteur de QCM">${ico('play', { taille: 16 })}${reussite != null ? 'Refaire' : 'Faire le sujet'}</button>
        </div>
    </div>`;
}

// ---------------------------------------------------------------- le cours : polycopié, LCA Pro, mémo
function panneauCours() {
    return html`${blocPolycopie()}${blocCoursPro()}${blocMemo()}`;
}

// Le sommaire du polycopié de la faculté (Sorbonne Université, Pr A. Dechartres, 128 pages) : numéro, intitulé, page.
// Le PDF n'a aucun signet ; c'est cette table qui permet de sauter au bon endroit (#page=N). Un autre polycopié
// s'affiche sans sommaire, sans rien casser.
var POLY_SOMMAIRE = [
    ['1', "1re partie : notions communes aux différents types d'article", 5],
    ['1.1', "Structure d'un article", 6],
    ['1.2', "Les différents types d'étude", 9],
    ['1.2.1', 'Les études comparatives', 9],
    ['1.2.2', 'Les études non-comparatives', 9],
    ['1.2.3', "Autres mots-clés concernant le plan expérimental", 10],
    ['1.3', "Constitution d'un échantillon, population source et population cible", 11],
    ['1.3.1', 'Echantillon, population source et population cible', 11],
    ['1.3.2', "Notion de fluctuations d'échantillonnage", 12],
    ['1.3.3', "Constitution de l'échantillon", 13],
    ['1.4', 'Biais et notion de validité interne', 13],
    ['1.4.1', 'Notion de biais', 13],
    ['1.4.2', 'Les principaux biais', 15],
    ['1.4.3', 'Quels sont les réflexes à avoir ?', 18],
    ['1.4.4', 'Définition de la validité interne', 18],
    ['1.5', 'Validité externe', 19],
    ['1.6', "Niveau de preuve d'une étude", 20],
    ['1.7', 'Notions de biostatistiques à connaître', 21],
    ['1.7.1', 'Les différents types de variable', 21],
    ['1.7.2', 'Analyse descriptive', 23],
    ['1.7.3', 'Analyse univariée', 29],
    ['1.7.4', 'Analyse multivariée', 36],
    ['1.7.5', 'Score de propension', 40],
    ['1.7.6', 'Test de tendance (trend test)', 46],
    ['1.7.7', "Test d'interaction", 47],
    ['2', "2e partie : spécificités des différents types d'articles", 50],
    ['2.1', "Évaluation d'un traitement", 51],
    ['2.1.1', "Formulation de l'objectif", 52],
    ['2.1.2', "Type d'étude", 52],
    ['2.1.3', "Choix du comparateur (de l'intervention dans le groupe contrôle)", 53],
    ['2.1.4', 'Randomisation', 54],
    ['2.1.5', "Maintien de la comparabilité des groupes pendant l'essai", 61],
    ['2.1.6', 'Les critères de jugement', 68],
    ['2.1.7', "Calcul d'effectif (ou calcul du nombre de sujets nécessaires)", 72],
    ['2.1.8', "Respect des règles éthiques et enregistrement des essais", 73],
    ['2.1.9', "Interprétation des résultats d'un essai", 75],
    ['2.1.10', 'Plans expérimentaux particuliers', 80],
    ['2.1.11', "Les différents comités dans un essai clinique", 84],
    ['2.1.12', 'Rappel des biais dans un essai contrôlé randomisé', 85],
    ['2.1.13', 'Recommandations de reporting pour les essais contrôlés randomisés', 86],
    ['2.2', 'Évaluation étiologique', 87],
    ['2.2.1', "Formulation de l'objectif", 88],
    ['2.2.2', "Type d'étude", 88],
    ['2.2.3', 'Particularités des études de cohorte', 91],
    ['2.2.4', 'Particularités des études cas-témoin', 93],
    ['2.2.5', 'Biais de classement dans les études observationnelles (cohorte et cas témoin)', 95],
    ['2.2.6', 'Prise en compte des facteurs de confusion potentiels', 96],
    ['2.2.7', 'Interprétation des résultats', 99],
    ['2.2.8', 'Causalité', 100],
    ['2.3', 'Évaluation de facteurs pronostiques', 104],
    ['2.3.1', "Formulation de l'objectif", 105],
    ['2.3.2', 'Distinction facteur de risque / facteur pronostique', 105],
    ['2.3.3', "Type d'étude utilisé", 106],
    ['2.3.4', 'Les biais dans les études pronostiques', 107],
    ['2.4', "Évaluation d'un test diagnostique", 108],
    ['2.4.1', "Formulation de l'objectif", 109],
    ['2.4.2', "Type d'étude", 109],
    ['2.4.3', "Population d'étude", 109],
    ['2.4.4', 'Choix du test de référence', 110],
    ['2.4.5', 'Réalisation des tests (test à évaluer et test de référence)', 110],
    ['2.4.6', "Evaluation des performances diagnostiques d'un test", 110],
    ['2.4.7', "Rappels des différents biais pouvant affecter une étude d'évaluation diagnostique", 117],
    ['2.4.8', 'Recommandations pour rapporter un article diagnostique', 117],
    ['2.5', "Évaluation d'une intervention de dépistage", 118],
    ['2.5.1', 'Définition', 118],
    ['2.5.2', "Formulation de l'objectif", 118],
    ['2.5.3', "Type d'étude", 119],
    ['2.5.4', 'Points à évaluer', 119],
    ['2.5.5', "Biais spécifiques aux études d'évaluation d'une procédure de dépistage", 120],
    ['2.5.6', "Critères justifiant la mise en place d'un dépistage organisé", 120],
    ['2.6', 'Revues systématiques et méta-analyses', 121],
    ['2.6.1', "Formulation de l'objectif", 122],
    ['2.6.2', "Type d'étude", 122],
    ['2.6.3', "Principaux intérêts d'une revue systématique et méta-analyse", 126],
    ['2.6.4', 'Principales limites', 127],
    ['2.6.5', 'Recommandations de reporting pour les revues systématiques et méta-analyses', 127],
];
function estLePoly(doc) { return !!doc && doc.ext === 'pdf' && /poly/i.test(doc.nom) && /lca/i.test(doc.nom); }
function pageDuChapitre(section) { var e = POLY_SOMMAIRE.find(function(x) { return x[0] === section; }); return e ? e[2] : null; }

function blocPolycopie() {
    var L = etat(), c = L.cours;
    var corps;
    if (L.coursBusy && !c) corps = html`<div class="squelettes" aria-hidden="true"><div class="squelette squelette-ligne" style="width:40%"></div><div class="squelette squelette-bloc" style="height:180px"></div></div>`;
    else if (L.coursErreur) corps = html`<p class="lca-erreur">${ico('warning-circle', { taille: 18 })}<span>${fr("Le polycopié n'a pas pu être lu : " + L.coursErreur + ". Réessaie dans un instant, ou actualise.")}</span></p>`;
    else if (!c || !(c.fichiers || []).length) corps = videPolycopie(c);
    else corps = polycopieHtml(c);
    return html`<section class="carte lca-bloc" aria-labelledby="lca-poly-titre"><div class="carte-corps">
        <div class="lca-bloc-entete">
            <h2 id="lca-poly-titre">Le polycopié</h2>
            <button type="button" class="btn btn-fantome btn-sm" data-action="lcaRafraichirCours"${L.coursBusy ? html` disabled aria-disabled="true" aria-busy="true"` : ''} title="Relire le dossier des documents de cours">Actualiser</button>
        </div>
        ${corps}
    </div></section>`;
}

function videPolycopie(c) {
    return html`<div class="vide">${ico('file-text')}
        <h2>Le polycopié n'est pas encore en ligne</h2>
        <p>Le mémo de méthode plus bas tient en une page : c'est lui qu'on relit la veille. Les treize chapitres sont aussi disponibles en podcast.</p>
        ${admin() ? html`<p class="lca-vide-admin">Dépôt : le polycopié va dans <code>edn_data/lca/</code> sur le serveur, ou dans le dossier Google Drive dont l'identifiant est <code>$lca_folder_id</code> (api.php) — partagé en lecture « Tous ceux qui ont le lien ».${c && c.drive_erreur ? html` <span class="lca-erreur-texte">Google Drive : ${c.drive_erreur}</span>` : ''}${c && !c.drive_configure ? ' Drive non configuré : seul le dossier du serveur est lu.' : ''}</p>` : ''}
    </div>`;
}

function polycopieHtml(c) {
    var L = etat();
    var doc = c.fichiers.find(function(f) { return f.cle === L.doc; }) || c.fichiers[0];
    var url = 'api.php?action=lca_fichier&c=' + encodeURIComponent(doc.cle);
    var page = estLePoly(doc) ? (L.page || 1) : 1;
    var image = doc.ext === 'png' || doc.ext === 'jpg' || doc.ext === 'jpeg' || doc.ext === 'webp';
    var vue = image
        ? html`<img class="lca-vue-image" src="${url}" alt="${doc.nom}">`
        : html`<iframe id="lca-visionneuse" class="lca-vue-cadre" src="${url}#page=${page}&view=FitH" title="${doc.nom}"></iframe>`;
    var som = estLePoly(doc) ? sommaireHtml() : '';
    return html`${c.fichiers.length > 1 ? html`<div class="lca-docs" role="group" aria-label="Documents de cours">${c.fichiers.map(function(f) {
            var on = f.cle === doc.cle;
            return html`<button type="button" class="btn btn-sm ${on ? 'btn-secondaire' : 'btn-fantome'} lca-doc" aria-pressed="${on ? 'true' : 'false'}" data-action="lcaDoc" data-args="${attr([f.cle])}" title="${f.source === 'drive' ? 'Google Drive' : 'Serveur'}">${ico(f.ext === 'pdf' ? 'file-pdf' : 'file-text', { taille: 16 })}${String(f.nom).replace(/\.[a-z0-9]+$/i, '')}${f.octets ? html`<span class="lca-doc-poids num">${Math.round(f.octets / 1024)} Ko</span>` : ''}</button>`;
        })}</div>` : ''}
        <div class="lca-lecture${som ? ' lca-lecture--sommaire' : ''}">
            ${som}
            <div class="lca-vue">
                ${vue}
                <div class="lca-vue-pied">
                    ${estLePoly(doc) ? html`<span class="num" id="lca-page-courante">page ${page}</span>` : ''}
                    <a class="btn btn-fantome btn-sm lca-plein-ecran" href="${url}" target="_blank" rel="noopener">${ico('arrow-square-out', { taille: 16 })}Ouvrir en plein écran</a>
                </div>
            </div>
        </div>`;
}

function sommaireHtml() {
    var L = etat();
    var lignes = POLY_SOMMAIRE.filter(function(e) {
        var n = e[0].split('.').length;
        if (n <= 2) return true;
        return !!L.section && e[0].indexOf(L.section + '.') === 0;   // niveau 3 : seulement sous la section ouverte
    });
    return html`<nav class="lca-sommaire" aria-label="Sommaire du polycopié">
        <h3 class="lca-sommaire-titre">Sommaire</h3>
        <ul class="lca-sommaire-liste">${lignes.map(function(e) {
            var num = e[0], titre = e[1], page = e[2], niveau = num.split('.').length;
            var aDesSous = niveau === 2 && POLY_SOMMAIRE.some(function(x) { return x[0].indexOf(num + '.') === 0; });
            var ouvert = L.section === num;
            return html`<li class="lca-sommaire-ligne lca-niveau-${niveau}">
                <button type="button" class="lca-sommaire-lien" data-action="lcaPage" data-args="${attr([page])}" title="Ouvrir le polycopié page ${page}"><span class="lca-sommaire-texte">${titre}</span><span class="lca-sommaire-page num">${page}</span></button>
                ${aDesSous ? html`<button type="button" class="ico-btn lca-sommaire-plier" data-action="lcaSection" data-args="${attr([num])}" aria-expanded="${ouvert ? 'true' : 'false'}" aria-label="${ouvert ? 'Replier' : 'Déplier'} le détail de « ${titre} »">${ico(ouvert ? 'caret-down' : 'caret-right', { taille: 16 })}</button>` : ''}
            </li>`;
        })}</ul>
    </nav>`;
}

// Le cours de LCA Pro : des pages HTML autonomes, servies par le serveur (les fichiers restent hors du site public).
var GROUPES_PRO = [['cours', 'Le cours, chapitre par chapitre'], ['fiches', 'Fiches réflexes'], ['blog', 'Guides']];
function blocCoursPro() {
    var L = etat();
    if (!L.pro) { if (window.lcaChargerCoursPro) window.lcaChargerCoursPro(); return ''; }
    if (!L.pro.length) return '';
    return html`<section class="carte lca-bloc" aria-labelledby="lca-pro-titre"><div class="carte-corps">
        <h2 id="lca-pro-titre">Le cours de LCA Pro</h2>
        <p class="lca-bloc-aide">${fr("Les pages du site, récupérées avec l'abonnement : méthode, biais, biostatistiques, figures. Elles s'ouvrent dans un onglet.")}</p>
        ${GROUPES_PRO.map(function(g) {
            var liste = L.pro.filter(function(p) { return p.type === g[0]; }).sort(function(a, b) { return (a.ordre || 0) - (b.ordre || 0); });
            if (!liste.length) return '';
            return html`<div class="lca-groupe">
                <h3 class="lca-groupe-titre">${g[1]}<span class="num">${liste.length}</span></h3>
                <div class="lca-groupe-liens">${liste.map(function(p, i) {
                    return html`<a class="btn btn-fantome btn-sm" href="api.php?action=lca_cours_page&f=${encodeURIComponent(p.fichier)}" target="_blank" rel="noopener" title="${p.intro || p.titre}">${g[0] === 'cours' ? html`<span class="num lca-lien-rang">${i + 1}</span>` : ''}${p.titre}${p.sections ? html`<span class="lca-lien-detail num">${p.sections} sections</span>` : ''}</a>`;
                })}</div>
            </div>`;
        })}
    </div></section>`;
}

// Le mémo de méthode : le socle qui s'apprend par cœur, en cinq blocs dépliables.
var MEMO = [
    { id: 'schemas', titre: "Les schémas d'étude", icone: 'tree-structure', resume: "Ce que chaque schéma permet de conclure, et ce qu'il ne permet pas.", lignes: [
        ['Essai contrôlé randomisé', "Seul schéma qui établit la causalité. Randomisation (tirage au sort), aveugle simple ou double, analyse en intention de traiter. Mesure : RR, RRA, NNT."],
        ['Cohorte (prospective)', "Exposés contre non exposés, suivis dans le temps. Donne l'incidence et le risque relatif. Sensible aux perdus de vue et aux facteurs de confusion."],
        ['Cas-témoins', "Malades contre non malades, exposition recherchée en arrière. Pour les maladies rares. Donne un odds ratio, jamais une incidence. Biais de mémorisation."],
        ['Transversale', "Une photographie à un instant. Donne la prévalence. Ne dit rien de la chronologie, donc rien de la cause."],
        ['Étude diagnostique', "Test évalué contre un examen de référence (gold standard), en aveugle. Donne Se, Sp, VPP, VPN, rapports de vraisemblance."],
        ['Méta-analyse', "Synthèse quantitative d'essais. À vérifier : stratégie de recherche, hétérogénéité (I²), biais de publication (funnel plot)."],
    ] },
    { id: 'mesures', titre: 'Les mesures et leur lecture', icone: 'calculator', resume: 'Les formules à refaire de tête, et le sens de chaque chiffre.', lignes: [
        ['Risque relatif (RR)', "Incidence des exposés sur incidence des non exposés. RR = 1 : pas d'effet. Son intervalle de confiance ne doit pas contenir 1 pour être significatif."],
        ['Odds ratio (OR)', "(a × d) / (b × c) sur le tableau 2 × 2. Approche le RR quand la maladie est rare. C'est la mesure des cas-témoins et de la régression logistique."],
        ['Hazard ratio (HR)', "Rapport des risques instantanés, issu de l'analyse de survie (Cox). Se lit sur les courbes de Kaplan-Meier."],
        ['RRA et NNT', "RRA = risque témoin − risque traité. NNT = 1 / RRA. Un NNT de 25 : il faut traiter 25 patients pour éviter un événement."],
        ['Se, Sp, VPP, VPN', "Se et Sp sont propres au test. VPP et VPN dépendent de la prévalence : le même test change de valeur prédictive selon la population."],
        ['Intervalle de confiance', "L'intervalle qui contient la vraie valeur dans 95 % des échantillons. Plus il est étroit, plus l'estimation est précise. Il remplace avantageusement le p."],
        ['p et puissance', "p < 0,05 : on rejette l'hypothèse nulle. La puissance (1 − β, visée à 80-90 %) est la capacité à détecter un effet qui existe. Un p non significatif sur une étude sous-dimensionnée ne prouve rien."],
    ] },
    { id: 'biais', titre: 'Les biais', icone: 'scales', resume: 'Trois familles, et ce qui prévient chacune.', lignes: [
        ['Biais de sélection', "L'échantillon ne représente pas la population visée : recrutement, volontariat, perdus de vue, exclusion après randomisation. Prévention : randomisation, analyse en intention de traiter, diagramme de flux des patients."],
        ['Biais de classement (mesure)', "L'information est recueillie différemment selon les groupes : mémorisation, évaluateur non aveugle, test non standardisé. Prévention : aveugle, procédure identique dans les deux groupes, critère de jugement objectif."],
        ['Biais de confusion', "Un tiers facteur explique l'association (l'âge, le tabac…). Prévention : randomisation, appariement, stratification, ajustement dans un modèle multivarié."],
        ['Autres pièges classiques', "Critère de jugement composite ou substitutif, analyses en sous-groupes non prévues, multiplicité des tests, arrêt prématuré de l'essai, conflits d'intérêts, absence d'enregistrement préalable du protocole."],
    ] },
    { id: 'grilles', titre: 'Les grilles de lecture', icone: 'list-checks', resume: "Une grille par type d'étude : les questions posées suivent leur plan.", lignes: [
        ['CONSORT', "Essais randomisés : diagramme de flux, méthode de randomisation, aveugle, critère principal défini a priori, intention de traiter."],
        ['STROBE', "Études observationnelles : source des données, définition de l'exposition et de l'événement, facteurs de confusion pris en compte."],
        ['STARD', "Études diagnostiques : population, examen de référence, lecture en aveugle, résultats indéterminés."],
        ['PRISMA', "Revues et méta-analyses : sources, sélection des articles, évaluation de la qualité, hétérogénéité, biais de publication."],
    ] },
    { id: 'reflexes', titre: 'Les réflexes qui rapportent des points', icone: 'lightning', resume: "Ce qu'on vérifie sur chaque article, quoi qu'il arrive.", lignes: [
        ['Le critère de jugement principal', "Un seul, défini avant l'étude, cliniquement pertinent. Un critère substitutif (la biologie à la place de la survie) affaiblit la conclusion."],
        ['La population analysée', "Elle doit correspondre à la population randomisée. Compter les perdus de vue : au-delà de 20 %, les résultats deviennent fragiles."],
        ['La cohérence des chiffres', "Refaire un NNT, un OR ou un pourcentage à la main. Vérifier que le texte, le résumé et les tableaux disent la même chose."],
        ['La portée de la conclusion', "Les auteurs concluent-ils au-delà de ce que leur schéma permet ? Une cohorte ne démontre pas une causalité, une association n'est pas un effet."],
        ["L'anglais de l'article", "Environ deux cents mots suffisent : outcome, endpoint, withdrawal, allocation concealment, blinding, hazard, crude et adjusted, accrual, baseline, follow-up."],
    ] },
];

function blocMemo() {
    var L = etat();
    return html`<section class="carte lca-bloc" id="lca-memo" aria-labelledby="lca-memo-titre"><div class="carte-corps">
        <h2 id="lca-memo-titre">Le mémo de méthode</h2>
        <p class="lca-bloc-aide">${fr("Le socle qui s'apprend par cœur, en cinq blocs. Le reste de la LCA est une compétence : elle se prend sur des articles, pas dans un cours.")}</p>
        <div class="lca-memo-blocs">${MEMO.map(function(b) {
            var ouvert = L.memo === b.id;
            return html`<div class="lca-memo-bloc${ouvert ? ' lca-memo-bloc--ouvert' : ''}" data-memo="${b.id}">
                <button type="button" class="lca-memo-tete" data-action="lcaMemo" data-args="${attr([b.id])}" aria-expanded="${ouvert ? 'true' : 'false'}" aria-controls="lca-memo-corps-${b.id}">
                    ${ico(b.icone, { taille: 20, classe: 'lca-memo-ico' })}
                    <span class="lca-memo-texte"><span class="lca-memo-nom">${b.titre}</span><span class="lca-memo-resume">${fr(b.resume)}</span></span>
                    ${ico('caret-right', { taille: 16, classe: 'lca-memo-caret' })}
                </button>
                <div class="lca-memo-corps" id="lca-memo-corps-${b.id}"${ouvert ? '' : html` inert`}>
                    <dl class="lca-memo-liste">${b.lignes.map(function(l) { return html`<dt>${l[0]}</dt><dd>${fr(l[1])}</dd>`; })}</dl>
                </div>
            </div>`;
        })}</div>
        <p class="lca-bloc-pied">${fr("Une LCA par semaine, chronomètre en main, puis deux à l'approche des épreuves. Après chaque sujet, note ton erreur dans sa catégorie : calcul, biais, lecture d'un tableau, vocabulaire. C'est cette liste qu'on relit, pas le cours.")}</p>
    </div></section>`;
}

// ---------------------------------------------------------------- les treize chapitres en podcast
function panneauPodcasts() {
    var chapitres = window.LCA_CHAPITRES || [];
    var prets = 0, avecScript = 0;
    chapitres.forEach(function(c) {
        var pc = window.lcaChapitrePodcast(c[0]);
        if (!pc) return;
        avecScript++;
        if (pc.status === 'ready') prets++;
    });
    return html`<section class="carte lca-bloc" aria-labelledby="lca-chapitres-titre"><div class="carte-corps">
        <div class="lca-bloc-entete">
            <h2 id="lca-chapitres-titre">Les treize chapitres</h2>
            ${prets ? html`<span class="puce ${prets === chapitres.length ? 'puce-ok' : 'puce-sans-point'} num" title="Chapitres dont l'audio est enregistré">${prets} / ${chapitres.length} enregistrés</span>` : ''}
        </div>
        <p class="lca-bloc-aide">${fr("Un épisode par chapitre du polycopié, lu par un narrateur : à écouter en marchant, ou en relisant le chapitre en parallèle. Le titre de gauche ouvre le polycopié à la bonne page.")}</p>
        ${!avecScript ? html`<div class="lca-bloc-note">
            <p class="lca-bloc-aide">${fr('Les épisodes ne sont pas encore enregistrés. Les treize chapitres restent ci-dessous : chacun ouvre le polycopié au bon endroit.')}</p>
            ${admin() ? html`<p class="lca-note-admin">Sur le serveur, <code>python3 scripts/lca_podcasts.py</code> écrit les scripts dans <code>edn_data/podcasts/401…413/</code>, puis chaque chapitre s'enregistre d'ici.</p>` : ''}
        </div>` : ''}
        <ul class="lca-chapitres">${chapitres.map(function(c) { return chapitreHtml(c); })}</ul>
    </div></section>`;
}

function chapitreHtml(c) {
    var n = c[0], section = c[1], titre = c[2];
    var pc = window.lcaChapitrePodcast(n);
    var page = pageDuChapitre(section);
    var joue = pc && window.player && window.player.key === window.podcastKey(n, pc.slug);
    var enLecture = joue && window.player.audio && !window.player.audio.paused && !window.player.audio.ended;
    var droite;
    if (pc && pc.status === 'ready') {
        droite = html`<button type="button" class="btn btn-secondaire btn-sm lca-chapitre-action" data-action="lcaEcouter" data-args="${attr([String(n), pc.slug, 'LCA ' + section + ' · ' + titre])}" title="Écouter l'épisode du chapitre ${section} (${window.podcastMinutes(pc)} min)">${ico(enLecture ? 'pause' : 'play', { taille: 16 })}${enLecture ? 'Pause' : (joue ? 'Reprendre' : 'Écouter')}<span class="lca-chapitre-min num">${window.podcastMinutes(pc)} min</span></button>`;
    } else if (pc) {
        droite = html`<button type="button" class="btn btn-fantome btn-sm lca-chapitre-action" data-action="lcaSonoriser" data-args="${attr([String(n)])}" title="Le script est écrit : il reste à en enregistrer l'audio">${ico('microphone', { taille: 16 })}${pc.status === 'audio' ? 'Audio en cours' : 'Sonoriser'}</button>`;
    } else {
        droite = html`<span class="lca-chapitre-absent" title="Le script de ce chapitre n'est pas encore écrit">Pas encore d'épisode</span>`;
    }
    return html`<li class="lca-chapitre">
        <button type="button" class="lca-chapitre-nom" data-action="lcaPage" data-args="${attr([page || 1])}"${page ? '' : html` disabled aria-disabled="true"`} title="${page ? 'Ouvrir le polycopié page ' + page : 'Ce chapitre n\'est pas repéré dans le sommaire'}">
            <span class="lca-chapitre-section num">${section}</span>
            <span class="lca-chapitre-titre">${titre}</span>
            ${pc ? html`<span class="lca-chapitre-detail num">${pc.duration ? window.fmtTime(pc.duration) : '≈ ' + (pc.estMin || 1) + ' min'} · ${pluriel(pc.turns || 0, 'réplique', 'répliques')}</span>` : ''}
        </button>
        ${droite}
    </li>`;
}

// ---------------------------------------------------------------- redessins partiels
function redessinerListe() {
    var L = etat();
    if (!estListe(L.onglet)) return;
    var b = document.getElementById('lca-liste'); if (!b) return;
    b.innerHTML = String(listeHtml(L.onglet));
    var compte = document.getElementById('lca-compte');
    var f = L.filtre.trim();
    var n = b.querySelectorAll('.lca-sujet[data-sujet]').length;
    var texte = texteCompte(n, f, L.onglet);
    if (compte) compte.textContent = texte;
    annoncerCompte(texte, !!f);
}
function redessinPrevu() {
    if (renduPrevu) return;
    renduPrevu = requestAnimationFrame(function() { renduPrevu = 0; redessinerListe(); });
}
// Le compte se met à jour à l'œil à chaque caractère frappé, mais ne s'annonce qu'après une pause : sinon le lecteur
// d'écran lit tous les états intermédiaires.
var annonceMinuteur = 0, annonceOuverte = false;
function annoncerCompte(texte, filtre) {
    if (!annonceOuverte) { if (!filtre) return; annonceOuverte = true; }
    if (annonceMinuteur) clearTimeout(annonceMinuteur);
    annonceMinuteur = setTimeout(function() {
        annonceMinuteur = 0;
        var el = document.getElementById('lca-compte-annonce');
        if (el) el.textContent = texte;
    }, 400);
}
function majEffacer() {
    var x = document.querySelector('.lca-effacer'); if (!x) return;
    if (etat().filtre) x.removeAttribute('hidden'); else x.setAttribute('hidden', '');
}
// Le focus rendu au champ après un redessin complet de la page (réponse du serveur pendant la frappe).
function rendreFocus() {
    var m = focusARestaurer; focusARestaurer = null;
    if (!m) return;
    var champ = document.getElementById('lca-recherche');
    if (!champ || champ === document.activeElement) return;
    try { champ.focus({ preventScroll: true }); champ.setSelectionRange(m.debut, m.fin); } catch (e) {}
}

// ---------------------------------------------------------------- actions
actions.lcaOnglet = function(o) {
    var L = etat();
    if (!POSTES.some(function(p) { return p[0] === o; })) return;
    L.onglet = o; L.filtre = '';
    if (o === 'cours' && !L.coursCharge && window.lcaChargerCours) window.lcaChargerCours();
    if ((o === 'cours' || o === 'podcasts') && state.podcastsConfigured === null && window.loadPodcastIndex) window.loadPodcastIndex(function() { window.renderContent(); });
    window.renderContent();
    window.scrollTo(0, 0);
    var onglet = document.getElementById('lca-poste-' + o);
    if (onglet) { try { onglet.focus({ preventScroll: true }); } catch (e) {} }
};
// Flèches dans la barre des postes : la navigation attendue d'un tablist.
document.addEventListener('keydown', function(ev) {
    var el = ev.target && ev.target.closest ? ev.target.closest('.lca-onglets [role="tab"]') : null;
    if (!el || ev.defaultPrevented) return;
    var pas = ev.key === 'ArrowRight' ? 1 : (ev.key === 'ArrowLeft' ? -1 : 0);
    if (!pas && ev.key !== 'Home' && ev.key !== 'End') return;
    ev.preventDefault();
    var i = POSTES.findIndex(function(p) { return p[0] === etat().onglet; });
    var suivant = ev.key === 'Home' ? 0 : (ev.key === 'End' ? POSTES.length - 1 : (i + pas + POSTES.length) % POSTES.length);
    actions.lcaOnglet(POSTES[suivant][0]);
});

actions.lcaFiltre = function(el) { etat().filtre = String(el.value || ''); majEffacer(); redessinPrevu(); };
actions.lcaEffacer = function() {
    etat().filtre = '';
    var champ = document.getElementById('lca-recherche'); if (champ) champ.value = '';
    majEffacer(); redessinerListe(); if (champ) champ.focus();
};
document.addEventListener('keydown', function(ev) {
    if (ev.key !== 'Escape' || ev.defaultPrevented) return;
    var el = ev.target; if (!el || el.id !== 'lca-recherche' || !el.value) return;
    ev.preventDefault(); actions.lcaEffacer();
});

// Déplier un bloc du mémo : seules des classes et des attributs changent, aucun redessin.
actions.lcaMemo = function(id, el) {
    var L = etat(), bloc = el && el.closest ? el.closest('.lca-memo-bloc') : null;
    var corps = bloc ? bloc.querySelector('.lca-memo-corps') : null;
    var ouvrir = L.memo !== id;
    if (bloc && corps) {
        var autre = document.querySelector('.lca-memo-bloc--ouvert');
        if (autre && autre !== bloc) {
            autre.classList.remove('lca-memo-bloc--ouvert');
            var t = autre.querySelector('.lca-memo-tete'); if (t) t.setAttribute('aria-expanded', 'false');
            var c = autre.querySelector('.lca-memo-corps'); if (c) c.setAttribute('inert', '');
        }
        bloc.classList.toggle('lca-memo-bloc--ouvert', ouvrir);
        el.setAttribute('aria-expanded', ouvrir ? 'true' : 'false');
        if (ouvrir) corps.removeAttribute('inert'); else corps.setAttribute('inert', '');
        L.memo = ouvrir ? id : null;
        return;
    }
    L.memo = ouvrir ? id : null;
    window.renderContent();
};
actions.lcaMemoComplet = function() {
    var L = etat();
    L.onglet = 'cours'; L.memo = L.memo || 'schemas';
    if (!L.coursCharge && window.lcaChargerCours) window.lcaChargerCours();
    window.renderContent();
    var cible = document.getElementById('lca-memo');
    if (cible) { try { cible.scrollIntoView({ block: 'start', behavior: mouvementReduit() ? 'auto' : 'smooth' }); } catch (e) { window.scrollTo(0, 0); } }
    else window.scrollTo(0, 0);
};

actions.lcaDoc = function(cle) { var L = etat(); L.doc = cle || null; L.page = 1; window.renderContent(); };
actions.lcaRafraichirCours = function() { if (window.lcaChargerCours) window.lcaChargerCours(true); };
actions.lcaSection = function(num) { var L = etat(); L.section = (L.section === num) ? null : num; window.renderContent(); };
actions.lcaPage = function(n) {
    var L = etat();
    L.page = parseInt(n, 10) || 1;
    // Appelé depuis les podcasts : le polycopié est dans l'autre poste, on y va.
    if (L.onglet !== 'cours') {
        L.onglet = 'cours';
        if (!L.coursCharge && window.lcaChargerCours) window.lcaChargerCours();
        window.renderContent(); window.scrollTo(0, 0);
        return;
    }
    var v = document.getElementById('lca-visionneuse');
    if (v) v.src = String(v.src).replace(/#.*$/, '') + '#page=' + L.page + '&view=FitH';
    var t = document.getElementById('lca-page-courante');
    if (t) t.textContent = 'page ' + L.page;
};

// Le livre : un panneau en ligne, pas une fenêtre. Les choix sont natifs (radios, case, segments) : changer un
// réglage ne redessine que la phrase de tête, le focus ne bouge jamais.
actions.lcaLivre = function() {
    var L = etat();
    L.livre = !L.livre;
    window.renderContent();
    // Une révélation rend toujours le focus à son bouton : « Annuler » ferme le panneau et ramène là d'où on venait.
    var bouton = document.querySelector('.lca-marche-actions [data-action="lcaLivre"]');
    if (bouton) { try { bouton.focus({ preventScroll: true }); } catch (e) {} }
    if (L.livre) { var p = document.getElementById('lca-livre'); if (p) { try { p.scrollIntoView({ block: 'nearest', behavior: mouvementReduit() ? 'auto' : 'smooth' }); } catch (e) {} } }
};
function majLivre() {
    var r = document.getElementById('lca-livre-resume');
    if (r) r.textContent = resumeLivre();
    var L = etat();
    document.querySelectorAll('#lca-livre .lca-option').forEach(function(o) {
        var input = o.querySelector('input[type=radio]');
        if (input) o.classList.toggle('lca-option--on', input.value === L.livreChoix);
    });
    document.querySelectorAll('#lca-livre .segments [role=tab]').forEach(function(b, i) {
        var on = DENSITES[i] && DENSITES[i][0] === L.livreDensite;
        b.setAttribute('aria-selected', on ? 'true' : 'false');
        b.setAttribute('tabindex', on ? '0' : '-1');
    });
}
actions.lcaLivreChoix = function(v) { etat().livreChoix = v; majLivre(); };
actions.lcaLivreArticles = function(el) { etat().livreArticles = !!(el && el.checked); majLivre(); };
actions.lcaLivreDensite = function(v, el) { etat().livreDensite = v; majLivre(); if (el && el.focus) { try { el.focus({ preventScroll: true }); } catch (e) {} } };
actions.lcaLivreOuvrir = function() {
    var L = etat();
    var type = L.livreChoix || 'tous', art = L.livreArticles ? '1' : '0';
    var dens = L.livreDensite === 'confort' ? 'confort' : 'compact';
    L.livre = false;
    window.renderContent();
    window.open('api.php?action=qcm_livre&mode=lca&type=' + type + '&articles=' + art + '&densite=' + dens, '_blank', 'noopener');
    if (window.toast) window.toast('Le livre s\'assemble dans un onglet : imprime-le depuis le navigateur', { icone: 'file-pdf' });
};

// La séance appartient au moteur de QCM : on l'appelle, on ne la rejoue pas.
actions.lcaFaire = function(n) { if (actions.qcmStartSession) actions.qcmStartSession(String(n), 'all', ''); };
actions.lcaCorrections = function(n) { if (actions.qcmOpenItem) actions.qcmOpenItem(String(n), '', ''); };
actions.lcaEcouter = function(n, slug, titre) {
    var faux = document.createElement('button');
    faux.setAttribute('data-pk', window.podcastKey(n, slug));
    faux.setAttribute('data-n', String(n)); faux.setAttribute('data-slug', slug); faux.setAttribute('data-title', titre);
    actions.playPodcastEl(faux);
};
actions.lcaSonoriser = function(n) { if (actions.openPodcast) actions.openPodcast(String(n), '', false, []); };

// Les douze actions reprises au rendu historique sont reposées ici sous leur nom complet. `actions` EST
// window.actions — ces lignes ne changent rien à l'exécution —, mais l'inventaire des globales
// (scripts/inventaire_globals.py) lit le texte des fichiers : sans elles, il croit les actions disparues.
window.actions.lcaOnglet = actions.lcaOnglet;
window.actions.lcaFiltre = actions.lcaFiltre;
window.actions.lcaMemo = actions.lcaMemo;
window.actions.lcaDoc = actions.lcaDoc;
window.actions.lcaRafraichirCours = actions.lcaRafraichirCours;
window.actions.lcaSection = actions.lcaSection;
window.actions.lcaPage = actions.lcaPage;
window.actions.lcaLivre = actions.lcaLivre;
window.actions.lcaLivreChoix = actions.lcaLivreChoix;
window.actions.lcaLivreArticles = actions.lcaLivreArticles;
window.actions.lcaLivreDensite = actions.lcaLivreDensite;
window.actions.lcaLivreOuvrir = actions.lcaLivreOuvrir;

// window.renderLcaTab remplace le rendu historique par son nom (renderContent l'appelle et pose sa sortie en
// innerHTML) : il rend une chaîne, comme le faisait celui de src/legacy/lca.js.
window.renderLcaTab = function() { return String(renderLca()); };
window.renderLca = renderLca;
